import { useState } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { Alert, Button, Stack } from '@mui/material';
import type { SxProps } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { LiveContextModeSwitch } from '@/components/events/detail/tabs/flows/grid/actions/live-context-mode-switch';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { getObjectId, resolveRefId } from '@/helpers/object.id.helper';
import { getOriginEventId } from '@/helpers/event.helper';
import { FlowDetailSectionHeader } from './flow-detail-section-header';
import { useInsufficientPlayoutHoursInfo } from '@/hooks/use-insufficient-playout-hours-info';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { EVENT_SUBSCRIPTION_PRODUCT_KEYS } from '@/constants/event-subscription-product-keys';
import EventManagePlanDialog from '@/components/events/plan/event-manage-plan-dialog';
import EventSubscriptionCheckoutModal from '@/components/events/plan/event-subscription-checkout.modal';

export interface FlowControlSectionHeaderProps {
  flow: FlowDto;
  eventSlotId?: string | null;
  flowQueryKey?: QueryKey;
  sx?: SxProps;
}

function sameFlowDocument(a: FlowDto, b: FlowDto): boolean {
  if (a.id === b.id) return true;
  const aOrigin = a.origin ? getObjectId(a.origin) : a.id;
  const bOrigin = b.origin ? getObjectId(b.origin) : b.id;
  return aOrigin === bOrigin;
}

/** Control tab section header: title left, SIMULATION/LIVE switch right. */
export function FlowControlSectionHeader({ flow, eventSlotId, flowQueryKey, sx }: FlowControlSectionHeaderProps) {
  const t = useTranslations();
  const storeFlow = useFlowStore((s) => s.flow);
  const event = useEventStore((s) => s.event);
  const title = t('flows.detail.editDetails.controlTitle');
  // Prefer store flow so SIMULATION/LIVE reflects WS patches before GET_FLOW refetch.
  const switchFlow = storeFlow && sameFlowDocument(storeFlow, flow) ? storeFlow : flow;
  const isLive = Boolean(switchFlow.liveContext?.isRunning);
  const { info: insufficientPlayout } = useInsufficientPlayoutHoursInfo(switchFlow);

  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');
  const monetizeEnabled = !monetizeAccessLoading && hasMonetizeAccess;
  const { data: permissions } = usePermissionsQuery();
  const eventId = getOriginEventId(event);
  const canUpdateSubscriptionPlan = Boolean(
    eventId && permissions?.events?.[eventId]?.includes(EVENT_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN),
  );
  const canManagePlan = monetizeEnabled && canUpdateSubscriptionPlan;
  const [manageOpen, setManageOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const flowScopeId = resolveRefId(switchFlow.origin) ?? switchFlow.id;
  const capacityProductKey =
    event?.multiFlow === true
      ? EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW
      : EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY;

  return (
    <Stack spacing={1} sx={sx}>
      <FlowDetailSectionHeader
        title={title}
        ariaLabel={title}
        accent={isLive ? 'warning' : 'secondary'}
        actions={<LiveContextModeSwitch flow={switchFlow} eventSlotId={eventSlotId} flowQueryKey={flowQueryKey} />}
        sx={{ mb: 0 }}
      />

      {insufficientPlayout && !isLive ? (
        <Alert
          severity="info"
          sx={{ py: 0.5 }}
          action={
            canManagePlan ? (
              <Button color="inherit" size="small" onClick={() => setManageOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                {t('event.plan.manage')}
              </Button>
            ) : undefined
          }
        >
          {t('event.plan.capacityPlayoutExceeds', {
            hours: insufficientPlayout.hours,
            minutes: insufficientPlayout.minutes,
            playoutHours: insufficientPlayout.playoutHoursLabel,
          })}
        </Alert>
      ) : null}

      {canManagePlan && eventId && flowScopeId ? (
        <EventManagePlanDialog
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          onGoToCart={() => {
            setManageOpen(false);
            setCheckoutOpen(true);
          }}
          eventId={eventId}
          subscriptionProductKey={capacityProductKey}
          scopeType="Flow"
          scopeId={flowScopeId}
        />
      ) : null}

      {canManagePlan && eventId ? (
        <EventSubscriptionCheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} eventId={eventId} />
      ) : null}
    </Stack>
  );
}
